import { yupResolver } from '@hookform/resolvers/yup'
import { find, map } from 'lodash-es'
import { ChangeEvent, useCallback, useEffect, useState } from 'react'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'
import * as yup from 'yup'

import { getTranslation } from '../../../account/services/auth'
import { Languages, TicketCategory } from '../../../common/models'
import { InviteTicketsList } from '../../../common/models/ticket'
import FormGroup from '../../../core/components/FormGroup'
import { getSettingsLanguages } from '../../../core/services/festival-settings'
import {
  CreateInvitePayload,
  createInvite,
  uploadCsv,
} from '../../../core/services/invites'
import {
  getAllCategories,
  getInviteTicketsByCategoryId,
} from '../../../core/services/tickets'
import translate from '../../../core/services/translate'
import { IUser, searchUsersByEmail } from '../../../core/services/users'
import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { MessageBox } from '../messages/MessageBox'
import { PageTitle } from '@/components'
import { Button, Checkbox, Combobox, Input, Modal } from '@/components/ui'
import SelectField from '@/components/select-field'
import { cn } from '@/lib/utils'
import { InfoIcon } from 'lucide-react'
import Spinner from '@/components/Spinner'

export type EmailContact = {
  id?: string
  firstName?: string
  lastName?: string
  phone?: string
  email: string
}
interface ICreateInviteValues {
  locale: string
  label: string
  category: string
  ticketId: string
  price: number
  freeCheckin: boolean
  nrOfInvites: number
  nominated: 'true' | 'false'
  nominateType?: string
  uploadKey?: string
  emails?: EmailContact[]
  generateType?: string
  receiverEmail?: string
  dubaiPriceTypeCode?: string
  intownTicketId?: string
  redeemTaxType?: string
  taxPrice?: number | null
  dubaiIntegration?: boolean
}

interface IIndexUsers {
  [index: number]: {
    users: IUser[]
    isLoading: boolean
    selectedUser?: IUser
  }
}

export const generateTypes = [
  { label: 'Email', value: 'SEND_EMAIL' },
  { label: 'Download', value: 'DOWNLOAD' },
]

export const nominateTypes = [
  { label: 'Upload', value: 'UPLOAD' },
  { label: 'Manual', value: 'MANUAL' },
]

export const redeemTaxTypes = [
  { label: 'Paid', value: 'PAID' },
  { label: 'Free', value: 'FREE' },
]

export interface CreateInviteProps {
  visible: boolean
  setVisible: (show: boolean) => void
  dubaiIntegration?: boolean
  onSuccess?: () => void
  currency: string
}

export const CreateInvite = (props: CreateInviteProps): JSX.Element => {
  const navigate = useNavigate()
  const { visible, setVisible, onSuccess, dubaiIntegration, currency } = props
  const { editionId, festivalName } = useParams()
  const [isError, setIsError] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const [correct, setCorrect] = useState<number>(0)
  const [faileds, setFaileds] = useState<string[]>([])
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const [usersData, setUsersData] = useState<IIndexUsers>({})
  const [showDeleteConfirmationModal, setDeleteConfirmationModal] =
    useState<boolean>(false)
  const [removeEmail, setRemoveEmail] = useState<number>(0)
  const [ticketCategoriesData, setTicketCategoriesData] = useState<
    TicketCategory[]
  >([])
  const [allLanguages, setAllLanguages] = useState<Languages[]>([])
  const [tickets, setTickets] = useState<InviteTicketsList[]>()

  const [submitting, setSubmitting] = useState<boolean>(false)

  const language = getTranslation()

  const getData = useCallback(async () => {
    if (!editionId) return
    try {
      const { data } = await getAllCategories({ size: 1000 })
      setTicketCategoriesData(data.content)
      const resp = await getSettingsLanguages()
      setAllLanguages(resp.data)
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    }
  }, [editionId])

  useEffect(() => {
    if (visible) {
      getData()
    }
    return reset()
  }, [visible, getData])

  const deleteFunc = () => {
    removeContact(removeEmail)
  }

  const handleSearch = async (
    index: number,
    query: string,
    signal: AbortSignal,
  ) => {
    const usersDataCopy: IIndexUsers = { ...usersData }
    usersDataCopy[index] = {
      users: usersDataCopy?.[index]?.users || [],
      isLoading: true,
    }
    setUsersData(usersDataCopy)
    try {
      const { data, status } = await searchUsersByEmail(query, signal)
      if (status === 200) {
        usersDataCopy[index] = {
          users: data,
          isLoading: false,
        }
        setUsersData(usersDataCopy)
      }
    } catch (e) {
      if (signal.aborted) return
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
      usersDataCopy[index] = {
        users: [],
        isLoading: false,
      }
      setUsersData(usersDataCopy)
    } finally {
      usersDataCopy[index] = {
        users: usersDataCopy?.[index]?.users || [],
        isLoading: false,
      }
      setUsersData(usersDataCopy)
    }
  }

  const handleClose = () => {
    setCorrect(0)
    setFaileds([])
    setVisible(false)
  }

  const schema = yup.object().shape({
    locale: yup.string().required('Label is required'),
    label: yup.string().required('Label is required'),
    category: yup.string().required('Category is required'),
    ticketId: yup.string().required('Ticket is required'),
    price: yup
      .number()
      .transform((v) => (isNaN(v) ? 0 : v))
      .required(),
    freeCheckin: yup.boolean().required(),
    nrOfInvites: yup.number().required('Number of invites is required'),
    nominated: yup.string().oneOf(['true', 'false']).required(),
    nominateType: yup.string().optional(),
    uploadKey: yup.string().when(['nominateType', 'nominated'], {
      is: (nominateType: string, nominated: string) =>
        (nominateType === 'UPLOAD' && nominated === 'true') ||
        (nominateType === '' && nominated === 'true'),
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.optional(),
    }),
    emails: yup
      .array()
      .of(
        yup.object().shape({
          id: yup.string().optional(),
          firstName: yup.string().optional(),
          lastName: yup.string().optional(),
          phone: yup.string().optional(),
          email: yup.string().required(),
        }),
      )
      .when('nominateType', {
        is: (val: string) => val === 'MANUAL',
        then: (schema) => schema.min(1),
        otherwise: (schema) => schema.optional(),
      }),
    generateType: yup.string().when('dubaiIntegration', {
      is: (val: boolean) => val,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.optional(),
    }),
    receiverEmail: yup
      .string()
      .email()
      .when(['generateType', 'nominated'], {
        is: (val: string, nominated: string) => {
          return nominated === 'false' && val === 'SEND_EMAIL'
        },
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.optional(),
      }),
    dubaiPriceTypeCode: yup.string().optional(),
    intownTicketId: yup.string().optional(),
    redeemTaxType: yup.string().required(),
    taxPrice: yup.number().when('redeemTaxType', {
      is: (val: string) => val === 'PAID',
      then: (schema) => schema.required(),
      otherwise: (schema) =>
        schema.optional().transform((v) => (isNaN(v) ? 0 : v)),
    }),
    dubaiIntegration: yup.boolean().optional(),
  })

  useEffect(() => {
    setValue('dubaiIntegration', dubaiIntegration, {
      shouldValidate: true,
    })
  }, [dubaiIntegration])

  const {
    handleSubmit,
    register,
    watch,
    setValue,
    control,
    formState: { errors },
    reset,
  } = useForm<ICreateInviteValues>({
    mode: 'onChange',
    resolver: yupResolver<ICreateInviteValues>(schema),
    defaultValues: {
      freeCheckin: false,
      nominated: 'false',
      nominateType: 'UPLOAD',
      redeemTaxType: 'FREE',
      locale: 'ro',
    },
  })

  const {
    fields: contactFields,
    append: appendContact,
    remove: removeContact,
  } = useFieldArray({
    control,
    name: 'emails',
  })

  const category = watch('category')
  const watchNominated = watch('nominated')
  const nominateType = watch('nominateType')
  const generateType = watch('generateType')
  const redeemTaxType = watch('redeemTaxType')
  const ticketIdWatch = watch('ticketId')

  const getCategoryTickets = useCallback(async () => {
    setValue('ticketId', '', {
      shouldValidate: true,
    })
    try {
      const { data } = await getInviteTicketsByCategoryId(category, {
        size: 1000,
      })
      setTickets(data.sort((a, b) => Number(b.published) - Number(a.published)))
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    }
  }, [category])

  useEffect(() => {
    if (category) {
      getCategoryTickets()
    }
  }, [category])

  const submit = async (values: ICreateInviteValues) => {
    const payload: CreateInvitePayload = {
      label: values.label,
      locale: values.locale,
      price: values.price,
      ticketId: values.ticketId,
      nrOfInvites: values.nrOfInvites,
      nominated: values.nominated === 'true',
      freeCheckin: values.freeCheckin,
    }

    if (values.nominated === 'true') {
      payload.nominateType = values.nominateType || 'UPLOAD'
    }

    if (values.generateType) {
      payload.generateType = values.generateType
    }

    if (values.uploadKey) {
      payload.uploadKey = values.uploadKey
    }

    if (values?.emails?.length) {
      payload.emails = map(values.emails, (contact) => contact.email)
    }
    if (values.receiverEmail) {
      payload.receiverEmail = values.receiverEmail
    }
    if (values.dubaiPriceTypeCode) {
      payload.dubaiPriceTypeCode = values.dubaiPriceTypeCode
    }
    if (values.intownTicketId) {
      payload.intownTicketId = values.intownTicketId
    }

    if (values.taxPrice) {
      payload.tax = values.taxPrice
    }
    if (values.redeemTaxType) {
      payload.taxType = values.redeemTaxType
    }
    try {
      setSubmitting(true)
      const { status } = await createInvite(payload)
      if (status === 200) {
        handleClose()
        if (values.generateType === 'DOWNLOAD') {
          navigate(`/rewards/invites/downloads/${festivalName}/${editionId}`)
        }
        onSuccess?.()
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      setIsError(true)
      setShowMessageBox(true)
      setMessage(e?.response?.data?.developerMessage)
    } finally {
      setSubmitting(false)
    }
  }

  const onChangeEmail = (userId: string, index: number) => {
    const user = usersData?.[index]?.users?.find((u) => u.id === userId)

    if (user) {
      setValue(`emails.${index}.phone`, user.phone)
      setValue(`emails.${index}.email`, user.email)
      setValue(`emails.${index}.firstName`, user.firstName)
      setValue(`emails.${index}.lastName`, user.lastName)
      setValue(`emails.${index}.id`, user.id)

      const usersDataCopy: IIndexUsers = { ...usersData }
      usersDataCopy[index] = {
        ...usersDataCopy[index],
        selectedUser: user,
      }
      setUsersData(usersDataCopy)
    }
  }

  const generateSelectClassBasedOnPublish = (ticket: string | undefined) => {
    if (!ticket || !tickets) return undefined
    const t = find(tickets, (tic) => tic.ticket.id === ticket)
    if (!t) return undefined

    if (t.published) {
      return 'select-green'
    } else {
      if (t.publishTimestamp - new Date().valueOf() <= 0) {
        return 'select-red'
      } else {
        return 'select-yellow'
      }
    }
  }

  const generateClassBasedOnPublish = (
    ticket: InviteTicketsList | undefined,
  ) => {
    if (!ticket) return undefined

    if (ticket.published) {
      return 'select-green'
    } else {
      if (ticket.publishTimestamp - new Date().valueOf() <= 0) {
        return 'select-red'
      } else {
        return 'select-yellow'
      }
    }
  }

  return (
    <>
      <Modal
        open={visible}
        onOpenChange={(open) => {
          if (!open) handleClose()
        }}
        size="xl"
        title={
          <div className="border-b border-monochrome-120 pb-4">
            <PageTitle title="Generate Tickets" />
          </div>
        }
        footer={
          <div className="flex w-full items-center gap-2 justify-end">
            <Button onClick={handleClose} type="button" variant="secondary">
              Cancel
            </Button>
            <Button
              form="generate-ticket-form"
              type="submit"
              variant="primary"
              loading={submitting}
            >
              {watchNominated === 'true'
                ? generateType === 'SEND_EMAIL'
                  ? 'Save & Send'
                  : 'Save & Download'
                : 'Generate'}
            </Button>
          </div>
        }
      >
        <form
          id="generate-ticket-form"
          onSubmit={handleSubmit(submit)}
          className="space-y-7"
        >
          <FormGroup
            label="Invite Language"
            tooltip="Invite will be generated in this Language"
            error={errors?.locale?.message}
          >
            <SelectField
              outline
              {...register(`locale`)}
              value={watch('locale') || ''}
              onValueChange={(value) => setValue('locale', value)}
              options={allLanguages.map((lang) => ({
                label: lang.name,
                value: lang.locale,
              }))}
              placeholder="Select Language"
              error={errors?.locale?.message}
            />
          </FormGroup>
          <FormGroup
            label="Label Name (* Only for internal use)"
            error={errors.label?.message}
          >
            <Input
              {...register('label')}
              type="text"
              isInvalid={!!errors.label?.message}
            />
          </FormGroup>
          <div className="flex flex-row items-start gap-3">
            <FormGroup
              label="Applied for Categories Tickets"
              tooltip={translate(
                language.value,
                'rewards.invites.appliedForTicketsTooltip',
              )}
              error={errors.category?.message}
            >
              <Controller
                name="category"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <SelectField
                    outline
                    value={value || ''}
                    onValueChange={onChange}
                    options={ticketCategoriesData.map((ticket) => ({
                      label: ticket.name,
                      value: ticket.id,
                    }))}
                    placeholder="Select Applied for Categories Tickets"
                    error={errors.category?.message}
                  />
                )}
              />
            </FormGroup>
            <FormGroup
              label="Select Ticket"
              tooltip="Select Ticket"
              error={errors.ticketId?.message}
            >
              <Controller
                name="ticketId"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <SelectField
                    outline
                    value={value || ''}
                    onValueChange={onChange}
                    options={
                      tickets?.map((ticket) => ({
                        label: `${ticket.ticket.name} - ${ticket.price} ${currency}`,
                        value: ticket.ticket.id,
                      })) || []
                    }
                    placeholder="Select Ticket"
                    error={errors.ticketId?.message}
                  />
                )}
              />
            </FormGroup>
          </div>
          <div className="flex justify-between gap-2">
            <FormGroup
              label="Ticket Price"
              wrapperClassName="w-full"
              error={errors.price?.message}
            >
              <Input
                {...register('price')}
                type="number"
                min={0}
                step={0.01}
                placeholder={`0 ${currency} (invited)`}
                isInvalid={!!errors.price?.message}
              />
            </FormGroup>
            <div className="w-full">
              <FormGroup
                label="Redeem Tax (Paid or Free)"
                error={errors.redeemTaxType?.message}
              >
                <SelectField
                  outline
                  {...register('redeemTaxType')}
                  value={watch('redeemTaxType') || 'FREE'}
                  onValueChange={(value) => setValue('redeemTaxType', value)}
                  options={redeemTaxTypes.map((type) => ({
                    label: type.label,
                    value: type.value,
                  }))}
                  error={errors.redeemTaxType?.message}
                />
              </FormGroup>
              {redeemTaxType === 'PAID' && (
                <FormGroup label="Tax Price" error={errors.taxPrice?.message}>
                  <Input
                    {...register('taxPrice')}
                    type="number"
                    min={0}
                    step={0.01}
                    placeholder="Tax Price"
                    isInvalid={!!errors.taxPrice?.message}
                  />
                </FormGroup>
              )}
            </div>
          </div>
          <FormGroup label="Free Checkin" direction="row">
            <Controller
              name="freeCheckin"
              control={control}
              render={({ field }) => (
                <Checkbox
                  checked={Boolean(field.value)}
                  onCheckedChange={(checked) =>
                    field.onChange(Boolean(checked))
                  }
                />
              )}
            />
          </FormGroup>

          {dubaiIntegration && (
            <>
              <div className="my-4 h-px w-full bg-monochrome-120" />
              <h5 className="text-xl font-semibold text-monochrome-1">
                Dubai DET Government API
              </h5>
              <FormGroup
                label="price type code"
                error={errors.dubaiPriceTypeCode?.message}
              >
                <Input
                  {...register('dubaiPriceTypeCode')}
                  type="text"
                  isInvalid={!!errors.dubaiPriceTypeCode?.message}
                />
              </FormGroup>
            </>
          )}
          {!dubaiIntegration && (
            <>
              <div className="my-4 h-px w-full bg-monochrome-120" />
              <FormGroup
                label="In Town API Code"
                error={errors.intownTicketId?.message}
              >
                <Input
                  {...register('intownTicketId')}
                  type="text"
                  placeholder="Enter In Town API Code"
                  isInvalid={!!errors.intownTicketId?.message}
                />
              </FormGroup>
            </>
          )}
          <FormGroup
            label="How many tickets to generate?"
            error={errors.nrOfInvites?.message}
          >
            <Input
              {...register('nrOfInvites')}
              min={0}
              type="number"
              isInvalid={!!errors.nrOfInvites?.message}
            />
          </FormGroup>
          <FormGroup
            label="Nominated or NOT?"
            error={errors.nominated?.message}
          >
            <Controller
              name="nominated"
              control={control}
              render={({ field: { value, onChange } }) => (
                <SelectField
                  outline
                  value={value || 'false'}
                  onValueChange={(val) => {
                    onChange(val)
                    if (val === 'true') {
                      setValue('nominateType', 'UPLOAD', {
                        shouldValidate: true,
                      })
                    }
                  }}
                  options={[
                    { label: 'Yes', value: 'true' },
                    { label: 'No', value: 'false' },
                  ]}
                  error={errors.nominated?.message}
                />
              )}
            />
          </FormGroup>
          {watchNominated === 'true' ? (
            <>
              <FormGroup label="Email List">
                <SelectField
                  outline
                  value={watch('nominateType') || 'UPLOAD'}
                  onValueChange={(value) => {
                    setValue('nominateType', value as 'UPLOAD' | 'MANUAL', {
                      shouldValidate: true,
                    })
                    if (value === 'MANUAL') {
                      setValue('emails', [
                        {
                          id: '',
                          firstName: '',
                          lastName: '',
                          phone: '',
                          email: '',
                        },
                      ])
                    } else {
                      setValue('emails', [])
                    }
                  }}
                  options={nominateTypes.map((type) => ({
                    label: type.label,
                    value: type.value,
                  }))}
                  error={errors.nominateType?.message}
                />
              </FormGroup>
              {nominateType === 'UPLOAD' ||
              (nominateType === '' && watch('nominated') === 'true') ? (
                <>
                  <FormGroup
                    label="Upload Email Addresses (CSV)"
                    error={errors?.uploadKey?.message}
                  >
                    <div className="flex items-center gap-2 w-full">
                      {isUploading && <Spinner />}
                      <Input
                        multiple={false}
                        accept=".csv"
                        type="file"
                        isInvalid={!!errors?.uploadKey?.message}
                        onChange={async (e: ChangeEvent<HTMLInputElement>) => {
                          const file = e?.target?.files?.[0]
                          if (file) {
                            setIsUploading(true)
                            setCorrect(0)
                            setFaileds([])
                            try {
                              const { data, status } = await uploadCsv(file)
                              if (status === 200) {
                                setValue('uploadKey', data.uploadKey, {
                                  shouldValidate: true,
                                })
                                setValue(
                                  'nrOfInvites',
                                  data.successfullyValidated,
                                  {
                                    shouldValidate: true,
                                  },
                                )
                                setCorrect(data.successfullyValidated)
                                setFaileds(data.failedEmails)
                              }
                            } catch (e) {
                              if (e instanceof Error) {
                                setIsError(true)
                                setShowMessageBox(true)
                                setMessage(e.message)
                              } else {
                                console.error(e)
                              }
                            } finally {
                              setIsUploading(false)
                            }
                          }
                        }}
                      />
                    </div>
                  </FormGroup>
                  <div className="flex flex-col gap-1">
                    {correct > 0 && (
                      <div
                        key={'primo'}
                        className="p-3 flex flex-row items-center bg-green-100 border border-green-300 w-full rounded-lg"
                      >
                        <InfoIcon className="w-4 h-4 mr-2 text-green-800" />
                        <span className="text-sm text-green-800 w-full">
                          {`${correct} emails successfully validated`}
                        </span>
                      </div>
                    )}
                    {faileds.length > 0 &&
                      map(faileds, (failed) => {
                        return (
                          <div
                            key={failed}
                            className="p-3 flex flex-row items-center bg-red-100 border border-red-300 w-full rounded-lg"
                          >
                            <InfoIcon className="w-4 h-4 mr-2 text-red-800" />
                            <span className="text-sm text-red-800 w-full">
                              {`${failed} is not valid`}
                            </span>
                          </div>
                        )
                      })}
                  </div>
                </>
              ) : (
                <>
                  <FormGroup
                    direction="column"
                    label="Select emails to send invitations"
                  >
                    <div className="flex flex-col w-full gap-3">
                      {map(contactFields, (field, index) => {
                        const selectedUser = usersData?.[index]?.selectedUser
                        return (
                          <div
                            key={field.id}
                            className="flex flex-col gap-2 w-full"
                          >
                            <div className="flex items-end gap-3 w-full">
                              <Controller
                                control={control}
                                name={`emails.${index}.id`}
                                render={({ field }) => (
                                  <Combobox
                                    value={field.value}
                                    onValueChange={(value) => {
                                      field.onChange(value)
                                      onChangeEmail(value, index)
                                    }}
                                    onSearch={(query, signal) =>
                                      handleSearch(index, query, signal)
                                    }
                                    isLoading={usersData?.[index]?.isLoading}
                                    minSearchLength={3}
                                    placeholder="Search user..."
                                    searchPlaceholder="Search user..."
                                    isInvalid={
                                      !!errors?.emails?.[index]?.id?.message
                                    }
                                    options={
                                      usersData?.[index]?.users?.map(
                                        (user) => ({
                                          label: user.email || '',
                                          value: user.id || '',
                                        }),
                                      ) || []
                                    }
                                    renderOption={(option) => {
                                      const user = usersData?.[
                                        index
                                      ]?.users?.find(
                                        (u) => u.id === option.value,
                                      )
                                      if (!user) return option.label
                                      return (
                                        <div>
                                          <span>
                                            {user.email}
                                            {user.phone?.length
                                              ? ` (${user.phone})`
                                              : ''}
                                          </span>
                                        </div>
                                      )
                                    }}
                                    className="w-full"
                                  />
                                )}
                              />
                              {contactFields.length > 1 && (
                                <Button
                                  variant="secondary"
                                  className="bg-accent-3 hover:bg-accent-3/90"
                                  onClick={() => {
                                    setDeleteConfirmationModal(true)
                                    setRemoveEmail(index)
                                  }}
                                  type="button"
                                >
                                  Delete
                                </Button>
                              )}
                            </div>
                            {selectedUser && (
                              <div className="p-3 rounded-lg bg-monochrome-5-50 border border-monochrome-120">
                                <p className="mb-0">{`${selectedUser.firstName} ${selectedUser.lastName}`}</p>
                                <p className="mb-0">{selectedUser.phone}</p>
                                <span>{selectedUser.email}</span>
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </FormGroup>
                  <Button
                    variant="secondary"
                    className="w-full"
                    onClick={() =>
                      appendContact({
                        id: '',
                        firstName: '',
                        lastName: '',
                        phone: '',
                        email: '',
                      })
                    }
                    type="button"
                  >
                    <span>Add New</span>
                    <i className="bi bi-plus-circle ml-2" />
                  </Button>
                </>
              )}
            </>
          ) : (
            <>
              <FormGroup
                label="Way to Send"
                error={errors.generateType?.message}
              >
                <SelectField
                  outline
                  {...register('generateType')}
                  value={watch('generateType') || ''}
                  onValueChange={(value) => setValue('generateType', value)}
                  options={generateTypes.map((way) => ({
                    label: way.label,
                    value: way.value,
                  }))}
                  error={errors.generateType?.message}
                />
              </FormGroup>
              {generateType === 'SEND_EMAIL' && (
                <FormGroup
                  direction="column"
                  label="Receiver email"
                  error={errors.receiverEmail?.message}
                >
                  <Input
                    {...register('receiverEmail')}
                    type="text"
                    placeholder="Enter Email"
                    isInvalid={!!errors.receiverEmail?.message}
                  />
                </FormGroup>
              )}
            </>
          )}
        </form>
      </Modal>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
      />
      <DeleteConfirmationModal
        showDeleteConfirmationModal={showDeleteConfirmationModal}
        setDeleteConfirmationModal={setDeleteConfirmationModal}
        deleteTitle={'Email'}
        deleteFunc={deleteFunc}
      />
    </>
  )
}
